import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { API } from '../App';
import { useClassroom } from '../contexts/ClassroomContext';
import { LaCourse } from '../games/LaCourse';
import { LaMemoire } from '../games/LaMemoire';
import { LaTour } from '../games/LaTour';
import { LAvalanche } from '../games/LAvalanche';
import { LeDuel } from '../games/LeDuel';
import { TriExpress } from '../games/TriExpress';
import { Batisseur } from '../games/Batisseur';
import { RobotBalayeur } from '../games/RobotBalayeur';
import { HorlogeFolle } from '../games/HorlogeFolle';
import { LeDetective } from '../games/LeDetective';
import { CarteDuCanada } from '../games/CarteDuCanada';
import { LeChef } from '../games/LeChef';
import { EndScreen } from '../components/EndScreen';
import { GameMenu } from '../components/GameMenu';
import { Maximize, Minimize, X, Monitor } from 'lucide-react';
import axios from 'axios';

const GAME_MAP = {
  la_course: LaCourse,
  memoire: LaMemoire,
  la_tour: LaTour,
  avalanche: LAvalanche,
  duel_conjugaison: LeDuel,
  tri_express: TriExpress,
  batisseur: Batisseur,
  robot_balayeur: RobotBalayeur,
  horloge_folle: HorlogeFolle,
  detective: LeDetective,
  carte_du_canada: CarteDuCanada,
  le_chef: LeChef,
};

export function UnifiedGamePlay() {
  const { game } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { activeClassroom } = useClassroom();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gameOver, setGameOver] = useState(false);
  const [results, setResults] = useState(null);
  const containerRef = useRef(null);
  const gameStartRef = useRef(null);
  const endedByGameRef = useRef(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showTeacherPanel, setShowTeacherPanel] = useState(false);
  const isClassroom = searchParams.get('classroom') === 'true';

  const mode = searchParams.get('mode') || 'school';

  // Classroom fullscreen mode
  useEffect(() => {
    if (!isClassroom) return;
    const enterFullscreen = async () => {
      try {
        const el = containerRef.current || document.documentElement;
        if (el.requestFullscreen) await el.requestFullscreen();
        else if (el.webkitRequestFullscreen) await el.webkitRequestFullscreen();
        setIsFullscreen(true);
      } catch {}
    };
    const timer = setTimeout(enterFullscreen, 500);
    return () => clearTimeout(timer);
  }, [isClassroom]);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const exitFullscreen = useCallback(() => {
    if (document.fullscreenElement) document.exitFullscreen?.();
    setIsFullscreen(false);
  }, []);

  const toggleFullscreen = useCallback(async () => {
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
    } else {
      const el = containerRef.current || document.documentElement;
      if (el.requestFullscreen) await el.requestFullscreen();
      setIsFullscreen(true);
    }
  }, []);

  const settings = useMemo(() => ({
    difficulty: searchParams.get('difficulty') || 'introductory',
    participation: searchParams.get('participation') || 'teams',
    rotation: searchParams.get('rotation') || 'each',
    rounds: parseInt(searchParams.get('rounds') || '10'),
    target: parseInt(searchParams.get('target') || '10'),
    gameTimer: parseInt(searchParams.get('gameTimer') || '0'),
    timer: parseInt(searchParams.get('timer') || '0'),
    team1Name: searchParams.get('team1Name') || 'Team 1',
    team2Name: searchParams.get('team2Name') || 'Team 2',
    gridSize: searchParams.get('gridSize') || '4x4',
    wordSpeed: parseFloat(searchParams.get('wordSpeed')) || 7,
    passageRepeat: parseInt(searchParams.get('passageRepeat') || '2'),
  }), [searchParams]);

  useEffect(() => {
    setLoading(true);
    setGameOver(false);
    setResults(null);
    endedByGameRef.current = false;

    // Lesson bank mode: fetch from dedicated per-lesson item bank
    const useLessonBank = searchParams.get('lessonBank') === '1';
    const lessonId = searchParams.get('lessonId');

    const fetchItems = useLessonBank && lessonId
      ? axios.get(`${API}/programs/lessons/${lessonId}/game-items/${game}`)
          .then(r => r.data.items || [])
          .catch(() => {
            // Fallback to shared pool if bank doesn't exist
            console.warn(`No lesson bank for ${lessonId}/${game}, falling back to shared pool`);
            return fetchFromSharedPool();
          })
      : fetchFromSharedPool();

    function fetchFromSharedPool() {
      // Build params for the unified browse endpoint (Browse Games path)
      const params = { mode, game, difficulty: settings.difficulty };
      if (mode === 'school') {
        params.grade = searchParams.get('grade');
        params.month = searchParams.get('month');
      }
      if (mode === 'level') {
        params.level = searchParams.get('level');
        const topic = searchParams.get('topic');
        if (topic) params.topic = topic;
      }
      if (mode === 'topic') {
        params.topic = searchParams.get('topic');
      }
      const itemId = searchParams.get('item_id');
      if (itemId) params.item_id = itemId;
      return axios.get(`${API}/content/browse`, { params }).then(r => {
        let data = r.data.items || [];
        if (data.length === 0) {
          const fallbackParams = { ...params };
          delete fallbackParams.difficulty;
          return axios.get(`${API}/content/browse`, { params: fallbackParams }).then(r2 => r2.data.items || []);
        }
        return data;
      });
    }

    fetchItems.then(data => {
      // Horloge Folle: filter by question type if specified
      const qType = searchParams.get('questionType');
      if (game === 'horloge_folle' && qType && qType !== 'all') {
        const typeMap = {
          numbers: ['see_produce_french'],
          time: ['see_clock_produce_french', 'time_reading', 'read_set_clock', 'time_with_future'],
          listening: ['hear_type_number', 'hear_type_time', 'hear_produce_time'],
          french_math: ['math_in_french'],
        };
        const allowedModes = typeMap[qType] || [];
        if (allowedModes.length > 0) data = data.filter(it => allowedModes.includes(it.mode));
      }
      // La Tour: filter by exercise type category if specified
      if (game === 'la_tour' && qType && qType !== 'all') {
        const typeMap = {
          articles: ['article_selection', 'preposition_selection'],
          conjugation: ['conjugation_drill', 'verb_selection', 'auxiliary_selection', 'agreement_selection'],
          tenses: ['tense_sorting', 'sentence_transformation'],
          vocabulary: ['word_image_speed_match', 'comparison_selection'],
        };
        const allowedTypes = typeMap[qType] || [];
        if (allowedTypes.length > 0) data = data.filter(it => allowedTypes.includes(it.exercise_type));
      }
      // Tri Express: filter by sort type category if specified
      if (game === 'tri_express' && qType && qType !== 'all') {
        const typeMap = {
          vocabulary: ['vocabulary_category'],
          gender: ['gender'],
          tenses: ['tense', 'tense_sorting'],
          grammar: ['comparison_type', 'conjunction_type', 'verb_type', 'auxiliary_selection', 'grammar_category', 'opinion_vs_fact', 'grammar', 'agreement_form', 'agreement_number', 'auxiliary_in_context'],
        };
        const allowedTypes = typeMap[qType] || [];
        if (allowedTypes.length > 0) data = data.filter(it => allowedTypes.includes(it.sort_type));
      }
      // Robot Balayeur: filter to selected passages if specified
      if (game === 'robot_balayeur') {
        const selectedIds = searchParams.get('selectedPassages');
        if (selectedIds) {
          const idSet = new Set(selectedIds.split(','));
          data = data.filter(it => idSet.has(it.item_id));
        }
      }
      // Shuffle (except Robot Balayeur — passages have pedagogical order)
      if (game !== 'robot_balayeur') {
        for (let i = data.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [data[i], data[j]] = [data[j], data[i]];
        }
      }
      // Cap items when launched from a lesson with a specific item count
      const lessonLimit = parseInt(searchParams.get('lessonItemCount'));
      if (lessonLimit && lessonLimit > 0 && data.length > lessonLimit) {
        data = data.slice(0, lessonLimit);
      }
      setItems(data);
      setLoading(false);
      gameStartRef.current = Date.now();
    }).catch(e => { console.error(e); setLoading(false); });
  }, [game, mode, searchParams, settings.difficulty]);

  const GAME_NAMES = {
    la_course: 'La Course', la_tour: 'La Tour', robot_balayeur: 'Robot Balayeur',
    tri_express: 'Tri Express', batisseur: 'Batisseur', detective: 'Le Detective',
    memoire: 'La Memoire', avalanche: "L'Avalanche", le_chef: 'Le Chef',
    carte_du_canada: 'Carte du Canada', duel_conjugaison: 'Le Duel',
    horloge_folle: "L'Horloge Folle",
  };

  const handleGameEnd = (res) => {
    endedByGameRef.current = true;
    setResults(res);
    setGameOver(true);

    // Auto-log session (teacher activity only, no student data)
    const duration = gameStartRef.current ? Math.round((Date.now() - gameStartRef.current) / 1000) : 0;
    const sessionData = {
      game,
      game_name: GAME_NAMES[game] || game,
      mode,
      difficulty: settings.difficulty,
      score: {},
      total_questions: res.totalQuestions || 0,
      duration_seconds: duration,
      config: Object.fromEntries(searchParams.entries()),
      classroom_id: activeClassroom?.id || null,
    };
    if (res.team1Score !== undefined) {
      sessionData.score = { team1: res.team1Score, team2: res.team2Score };
    } else if (res.score !== undefined) {
      sessionData.score = { correct: res.score, total: res.totalQuestions };
    }
    axios.post(`${API}/teacher/sessions`, sessionData).catch(() => {});
  };

  const handlePlayAgain = () => {
    setGameOver(false);
    setResults(null);
    endedByGameRef.current = false;
    setItems(prev => {
      const copy = [...prev];
      for (let i = copy.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [copy[i], copy[j]] = [copy[j], copy[i]];
      }
      return copy;
    });
  };

  const handleNewGame = () => {
    // Go back to game select with the same filter params
    const params = new URLSearchParams();
    params.set('mode', mode);
    if (mode === 'school') {
      params.set('grade', searchParams.get('grade'));
      params.set('month', searchParams.get('month'));
    }
    if (mode === 'level') {
      params.set('level', searchParams.get('level'));
      const topic = searchParams.get('topic');
      if (topic) params.set('topic', topic);
    }
    if (mode === 'topic') {
      params.set('topic', searchParams.get('topic'));
      const diff = searchParams.get('difficulty');
      if (diff) params.set('difficulty', diff);
    }
    navigate(`/games?${params}`);
  };

  const handleSkipToEnd = () => {
    // Demo/teacher shortcut: ask the active game to compute and submit
    // its current in-progress results. Each game listens for this event
    // and calls onGameEnd with whatever real numbers it has so far.
    window.dispatchEvent(new CustomEvent('frenchboard:end-game-now'));
    // Fallback: if the game has no listener (or no progress to report),
    // jump to the End Screen with empty results after a short grace period.
    setTimeout(() => {
      if (!endedByGameRef.current && !gameOver) {
        setResults({
          skipped: true,
          score: 0,
          totalQuestions: 0,
          accuracy: 0,
          bestStreak: 0,
        });
        setGameOver(true);
      }
    }, 1800);
  };

  const handleExit = () => {
    exitFullscreen();
    const returnTo = searchParams.get('returnTo');
    const returnSlide = searchParams.get('returnSlide');
    if (returnTo) {
      navigate(returnTo + (returnSlide ? `?slide=${returnSlide}` : ''));
    } else {
      navigate('/browse');
    }
  };

  if (loading) {
    return (
      <div ref={containerRef} data-testid="game-loading" style={{
        height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--bg)', flexDirection: 'column', gap: '1rem',
      }}>
        <div style={{
          width: 48, height: 48, border: '4px solid var(--surface-el)',
          borderTopColor: 'var(--primary)', borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <div style={{ fontFamily: 'Fredoka', fontSize: '1.5rem', color: 'var(--text-secondary)' }}>Loading...</div>
      </div>
    );
  }

  if (gameOver && results) {
    return (
      <div ref={containerRef}>
        <EndScreen results={results} settings={settings} gameName={game} items={items} onPlayAgain={handlePlayAgain} onNewGame={handleNewGame} onExit={handleExit} />
      </div>
    );
  }

  const GameComponent = GAME_MAP[game];
  if (GameComponent) {
    return (
      <div ref={containerRef} style={{ position: 'relative', height: '100vh' }}>
        <GameComponent items={items} settings={settings} onGameEnd={handleGameEnd} />
        <GameMenu onSkip={handleSkipToEnd} onExit={handleExit} />
        {/* Teacher floating controls — visible on hover in top-right corner */}
        {isClassroom && (
          <div
            data-testid="classroom-teacher-panel"
            onMouseEnter={() => setShowTeacherPanel(true)}
            onMouseLeave={() => setShowTeacherPanel(false)}
            style={{
              position: 'fixed', top: 12, right: 12, zIndex: 9999,
              display: 'flex', alignItems: 'center', gap: '0.5rem',
            }}
          >
            {/* Subtle hover trigger */}
            <div style={{
              width: 36, height: 36, borderRadius: '50%',
              background: showTeacherPanel ? 'rgba(30,33,64,0.9)' : 'rgba(30,33,64,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', transition: 'all 0.2s',
            }}>
              <Monitor size={16} color={showTeacherPanel ? 'white' : 'rgba(30,33,64,0.4)'} />
            </div>

            {showTeacherPanel && (
              <div style={{
                display: 'flex', gap: '0.375rem', background: 'rgba(30,33,64,0.95)',
                borderRadius: '0.625rem', padding: '0.375rem 0.5rem',
                boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
              }}>
                <button
                  data-testid="classroom-toggle-fullscreen"
                  onClick={toggleFullscreen}
                  title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
                  style={teacherBtnStyle}
                >
                  {isFullscreen ? <Minimize size={16} /> : <Maximize size={16} />}
                </button>
                <button
                  data-testid="classroom-exit-game"
                  onClick={handleExit}
                  title="End Game"
                  style={{ ...teacherBtnStyle, color: '#FF6B6B' }}
                >
                  <X size={16} />
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{
      height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--bg)', flexDirection: 'column', gap: '1.5rem',
    }}>
      <h2 style={{ fontFamily: 'Fredoka', fontSize: '2rem', color: 'var(--text-primary)' }}>
        {game} — Coming Soon
      </h2>
      <button className="btn-arcade btn-primary" onClick={handleNewGame} style={{ fontSize: '1.5rem' }}>
        Back to Games
      </button>
    </div>
  );
}

const teacherBtnStyle = {
  background: 'transparent', border: 'none', cursor: 'pointer',
  color: 'white', padding: '6px', borderRadius: '0.375rem',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  transition: 'background 0.15s',
};
